import { action, computed, makeObservable, observable } from "mobx"

import type { ManualClPoolTicksResponseItem } from "@/domains/validators"

/**
 * Represents a single "bin" in the liquidity distribution chart used by the
 * range-brush. Each bin covers a contiguous slice of tick space equal to
 * `tickSpacing` and carries the *active* liquidity inside that slice.
 */
export interface ThickV2TickDistributionBin {
  tickLower: number
  tickUpper: number
  /** Active liquidity at the *upper* end of this bin, as a bigint sum of
   * `liquidityNet` values processed in ascending tickIdx order. */
  activeLiquidity: bigint
}

/**
 * Observable store for a single pool's tick distribution.
 *
 * Shape of data: ticks are raw GraphQL rows (`tickIdx`, `liquidityNet`); the
 * `bins` computed transforms them into fixed-width buckets spanning the
 * configured viewport window. All conversions run lazily inside MobX, so UI
 * reads always see consistent values.
 *
 * The store itself is deliberately thin: it does no fetching. The orchestrator
 * owns the `OrchestratedQuery` that calls `setTicks(...)` on this store.
 */
export class ThickV2TickDistributionStore {
  ticks: Array<ManualClPoolTicksResponseItem> = []
  activePoolAddress: string | undefined = undefined
  tickSpacing: number = 60
  isLoading: boolean = false
  /**
   * Viewport in tick space. Bins outside this window are still computed but
   * filtered out at the render layer via `binsInWindow`.
   */
  windowLowerTick: number | undefined = undefined
  windowUpperTick: number | undefined = undefined

  constructor() {
    makeObservable(this, {
      ticks: observable,
      activePoolAddress: observable,
      tickSpacing: observable,
      isLoading: observable,
      windowLowerTick: observable,
      windowUpperTick: observable,

      setActivePool: action,
      setTickSpacing: action,
      setTicks: action,
      setWindow: action,
      setIsLoading: action,
      reset: action,

      sortedTicks: computed,
      bins: computed,
      binsInWindow: computed,
      totalActiveLiquidity: computed,
    })
  }

  setActivePool(poolAddress: string | undefined, tickSpacing: number): void {
    const nextAddress = poolAddress?.toLowerCase()
    if (nextAddress === this.activePoolAddress && this.tickSpacing === tickSpacing) return
    this.activePoolAddress = nextAddress
    this.tickSpacing = tickSpacing
    this.ticks = []
    this.windowLowerTick = undefined
    this.windowUpperTick = undefined
  }

  setTickSpacing(tickSpacing: number): void {
    if (tickSpacing === this.tickSpacing) return
    this.tickSpacing = tickSpacing
  }

  setTicks(ticks: Array<ManualClPoolTicksResponseItem>): void {
    this.ticks = ticks
  }

  /**
   * Set the viewport window (inclusive lower, exclusive upper). Views typically
   * expand the window to cover the currently-selected range plus some padding.
   */
  setWindow(tickLower: number | undefined, tickUpper: number | undefined): void {
    this.windowLowerTick = tickLower
    this.windowUpperTick = tickUpper
  }

  setIsLoading(isLoading: boolean): void {
    this.isLoading = isLoading
  }

  reset(): void {
    this.ticks = []
    this.activePoolAddress = undefined
    this.isLoading = false
    this.windowLowerTick = undefined
    this.windowUpperTick = undefined
  }

  get sortedTicks(): Array<{ tickIdx: number; liquidityNet: bigint }> {
    return this.ticks
      .map((tick) => ({
        tickIdx: Number(tick.tickIdx),
        liquidityNet: this.parseLiquidity(tick.liquidityNet),
      }))
      .sort((a, b) => a.tickIdx - b.tickIdx)
  }

  /**
   * Compute active-liquidity bins of width `tickSpacing`, running the cumulative
   * `liquidityNet` sum over `sortedTicks` in ascending order.
   *
   * The bin's `activeLiquidity` is the cumulative value *after* all ticks whose
   * `tickIdx <= bin.tickUpper` have been applied — matching the Uniswap v3
   * "active liquidity between two ticks" convention.
   */
  get bins(): Array<ThickV2TickDistributionBin> {
    const sorted = this.sortedTicks
    if (sorted.length === 0) return []
    const spacing = this.tickSpacing
    if (spacing <= 0) return []

    const firstTick = Math.floor(sorted[0]!.tickIdx / spacing) * spacing
    const lastTick = Math.ceil(sorted[sorted.length - 1]!.tickIdx / spacing) * spacing
    const bins: Array<ThickV2TickDistributionBin> = []

    let active = 0n
    let idx = 0

    for (let tick = firstTick; tick <= lastTick; tick += spacing) {
      while (idx < sorted.length && sorted[idx]!.tickIdx <= tick) {
        active += sorted[idx]!.liquidityNet
        idx++
      }
      bins.push({
        tickLower: tick - spacing,
        tickUpper: tick,
        activeLiquidity: active > 0n ? active : 0n,
      })
    }

    return bins
  }

  /**
   * Bins clipped to the configured viewport window. Falls back to all bins
   * when no window has been set.
   */
  get binsInWindow(): Array<ThickV2TickDistributionBin> {
    const lower = this.windowLowerTick
    const upper = this.windowUpperTick
    if (lower === undefined || upper === undefined) return this.bins
    return this.bins.filter((bin) => bin.tickUpper > lower && bin.tickLower < upper)
  }

  get totalActiveLiquidity(): bigint {
    return this.bins.reduce((sum, bin) => sum + bin.activeLiquidity, 0n)
  }

  /**
   * Cumulative active liquidity at `tickIdx` — the sum of `liquidityNet`
   * values for every initialised tick whose `tickIdx <= tickIdx`.
   *
   * This is the Uniswap v3 "active L at tick" quantity: the virtual
   * liquidity straddling that tick, and the correct denominator for a
   * position's fees-share calculation.
   *
   * Intentionally not a MobX computed — it takes an argument. Consumers
   * calling from a reactive context (computed / observer) will still
   * track `ticks` through `sortedTicks`.
   */
  activeLiquidityAt(tickIdx: number): bigint {
    const sorted = this.sortedTicks
    if (sorted.length === 0) return 0n

    let lo = 0
    let hi = sorted.length
    while (lo < hi) {
      const mid = (lo + hi) >>> 1
      if (sorted[mid]!.tickIdx <= tickIdx) {
        lo = mid + 1
      } else {
        hi = mid
      }
    }

    let active = 0n
    for (let i = 0; i < lo; i++) {
      active += sorted[i]!.liquidityNet
    }
    return active > 0n ? active : 0n
  }

  private parseLiquidity(raw: string): bigint {
    try {
      return BigInt(raw)
    } catch {
      return 0n
    }
  }
}
